import { Box, Grid, Typography, LinearProgress } from '@mui/material';
import styled from 'styled-components';
import { ReactComponent as Logo } from '../../logo.svg';

const RootContainer = styled(Box)`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #232933;
  padding: 0;
  @media (min-width: 600px) {
    padding: 0;
  }
`;

const BrandingBox = styled(Box)`
  background: #377dff;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border-radius: 24px 0 0 24px;
  @media (max-width: 900px) {
    border-radius: 24px 24px 0 0;
    min-height: 200px;
    padding: 24px 0;
  }
  @media (max-width: 600px) {
    min-height: 180px;
    padding: 16px 0;
  }
`;

const ContentBox = styled(Box)`
  background: #232933;
  height: 100%;
  display: flex;
  flex-direction: column;
  padding: 10px 0;
  border-radius: 0 24px 24px 0;
  min-height: 600px;
  @media (max-width: 900px) {
    border-radius: 0 0 24px 24px;
    padding: 32px 16px;
    min-height: auto;
  }
  @media (max-width: 600px) {
    padding: 10px 0px;
  }
`;

const Card = styled(Box)`
  width: 100%;
  max-width: 1200px;
  border-radius: 24px;
  // box-shadow: 0 8px 32px 0 rgba(31, 38, 135, 0.37);
  overflow: hidden;
  background: #232933;
`;

const StepIndicator = styled(Box)`
  display: flex;
  gap: 8px;
  margin-bottom: 24px;
  align-items: center;
`;

const StepNumber = styled(Box).withConfig({
  shouldForwardProp: (prop) => !['active', 'completed'].includes(prop),
})<{ active?: boolean; completed?: boolean }>`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 14px;
  background: ${props => 
    props.active ? '#377dff' : 
    props.completed ? '#377dff' : 
    '#4a5568'};
  color: ${props => 
    props.active || props.completed ? '#fff' : '#a0aec0'};
  transition: all 0.3s ease;
`;

const StepLine = styled(Box).withConfig({
  shouldForwardProp: (prop) => prop !== 'completed',
})<{ completed?: boolean }>`
  height: 2px;
  width: 40px;
  background: ${props => props.completed ? '#377dff' : '#4a5568'};
  transition: all 0.3s ease;
  
  @media (max-width: 600px) {
    width: 20px;
  }
`;

interface OnboardingLayoutProps {
  children: React.ReactNode;
  currentStep: number;
  totalSteps: number;
  stepTitle: string;
  stepSubtitle?: string;
}

const OnboardingLayout = ({ 
  children, 
  currentStep, 
  totalSteps, 
  stepTitle, 
  stepSubtitle 
}: OnboardingLayoutProps) => {
  const progress = (currentStep / totalSteps) * 100;

  return (
    <RootContainer>
      <Card>
        <Grid container sx={{ minHeight: { xs: 'auto', md: 600 } }}>
          {/* <Grid size={{xs:12, md:5}}>
            <BrandingBox>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <Logo style={{ width: 'auto', height: 'auto', maxWidth: '200px', maxHeight: '60px' }} />
              </Box>
            </BrandingBox>
          </Grid> */}
          <Grid size={{xs:12, md:12}}>
            <ContentBox>
              {/* Progress Bar */}
              <Box sx={{ mb: 3 }}>
                <LinearProgress 
                  variant="determinate" 
                  value={progress} 
                  sx={{
                    height: 4,
                    borderRadius: 2,
                    backgroundColor: '#4a5568',
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: '#377dff',
                      borderRadius: 2,
                    }
                  }}
                />
              </Box>

              {/* Step Indicators */}
              <StepIndicator>
                {Array.from({ length: totalSteps }, (_, index) => (
                  <Box key={index} sx={{ display: 'flex', alignItems: 'center' }}>
                    <StepNumber
                      active={currentStep === index + 1}
                      completed={currentStep > index + 1}
                    >
                      {index + 1}
                    </StepNumber>
                    {index < totalSteps - 1 && (
                      <StepLine completed={currentStep > index + 1} />
                    )}
                  </Box>
                ))}
              </StepIndicator>

              {/* Step Header */}
              <Box sx={{ mb: 3 }}>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: '#377dff', 
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}
                >
                  STEP {currentStep}
                </Typography>
                <Typography 
                  variant="h4" 
                  sx={{ 
                    color: '#fff', 
                    fontWeight: 700, 
                    mb: stepSubtitle ? 0.5 : 0,
                    fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' },
                    lineHeight: 1.2
                  }}
                >
                  {stepTitle}
                </Typography>
                {stepSubtitle && (
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      color: '#b0b8c1', 
                      fontSize: { xs: '0.875rem', sm: '1rem' }
                    }}
                  >
                    {stepSubtitle}
                  </Typography>
                )}
              </Box>

              {/* Content */}
              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                {children}
              </Box>
            </ContentBox>
          </Grid>
        </Grid>
      </Card>
    </RootContainer>
  );
};

export default OnboardingLayout;
